'use client';

import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

const CONTENT = {
  en: {
    title: 'Open Privacy Policy',
    lastUpdated: 'Last updated: September 28, 2026',
    introA:
      'This Privacy Policy explains how OpenChat, Inc. ("Open," "we," "us," or "our") collects, uses, shares, and protects personal data when you visit',
    introB:
      ', use our AI customer support platform, or otherwise interact with our services. It describes the categories of information we process, why we process them, the legal bases we rely on, how long we retain the data, the third parties we share it with, and the rights you have to access, correct, delete, or restrict our use of your information. Open is committed to handling personal data transparently and in compliance with global privacy regulations, including the EU GDPR, the UK GDPR, the Swiss FADP, the California Consumer Privacy Act (CCPA/CPRA), and similar laws around the world. We process personal data only where a legal basis applies — your consent, the performance of a contract, our legitimate interests, or a legal obligation — and where we rely on consent (such as for non-essential cookies), you can withdraw it at any time. If you have questions about any part of this policy, contact our Privacy Officer at',
    introC: 'with any questions or concerns.',
    catH2: 'Categories of Personal Data We Collect',
    catIntro:
      'We collect and process the following categories of personal data:',
    catH31: 'Information You Provide Directly',
    catAccountLabel: 'Account Information:',
    catAccount:
      'Name, email address, phone number, company name, job title, and password when you create an account or request a demo.',
    catPaymentLabel: 'Payment Information:',
    catPayment:
      'Billing address, payment card details (processed by our payment processors), and transaction history.',
    catCommsLabel: 'Communications:',
    catComms:
      'Content of messages, support tickets, and feedback you send to us.',
    catSupportLabel: 'Customer Support Data:',
    catSupport:
      'Information contained in customer support conversations processed through our platform on behalf of our business customers.',
    catH32: 'Information Collected Automatically',
    catDeviceLabel: 'Device Information:',
    catDevice:
      'IP address, browser type, operating system, device identifiers, and hardware settings.',
    catUsageLabel: 'Usage Data:',
    catUsage:
      'Pages visited, features used, time spent on pages, click patterns, and referring URLs.',
    catCookiesLabel: 'Cookies and Similar Technologies:',
    catCookies:
      'Session cookies, persistent cookies, and similar tracking technologies as described in our',
    catCookiesLinkText: 'Cookie Policy',
    catH33: 'Information from Third Parties',
    catIntegrationLabel: 'Integration Data:',
    catIntegration:
      'Information from third-party services you connect to Open (e.g., Zendesk, Intercom, Salesforce).',
    catBusinessLabel: 'Business Contact Information:',
    catBusiness:
      'Professional contact details from business partners or publicly available sources.',
    purposesH2: 'Purposes for Processing Personal Data',
    purposesIntro:
      'We process personal data for the following purposes. For each purpose, we state the legal basis we rely on under the EU GDPR and UK GDPR:',
    purposeServiceLabel: 'Service Delivery:',
    purposeService:
      'To provide, maintain, and improve our AI customer support platform and related services. Legal basis: performance of a contract (Article 6(1)(b) GDPR).',
    purposeAccountLabel: 'Account Management:',
    purposeAccount:
      'To create and manage user accounts, authenticate users, and process transactions. Legal basis: performance of a contract (Article 6(1)(b) GDPR).',
    purposeSupportLabel: 'Customer Support:',
    purposeSupport:
      'To respond to inquiries, provide technical assistance, and resolve issues. Legal basis: performance of a contract and our legitimate interests in providing effective support (Article 6(1)(b) and 6(1)(f) GDPR).',
    purposeCommsLabel: 'Communications:',
    purposeComms:
      'To send service-related notices, updates, security alerts, and administrative messages. Legal basis: performance of a contract and our legitimate interests in keeping users informed about the service (Article 6(1)(b) and 6(1)(f) GDPR).',
    purposeMarketingLabel: 'Marketing:',
    purposeMarketing:
      'To send promotional communications (with your consent where required) which you may opt out of at any time. Legal basis: your consent (Article 6(1)(a) GDPR), which you may withdraw at any time.',
    purposeAnalyticsLabel: 'Analytics and Improvement:',
    purposeAnalytics:
      'To analyze usage patterns, troubleshoot problems, and enhance our services. Legal basis: our legitimate interests in understanding and improving how the service is used (Article 6(1)(f) GDPR), and consent where required for non-essential cookies.',
    purposeSecurityLabel: 'Security and Fraud Prevention:',
    purposeSecurity:
      'To detect, prevent, and respond to security incidents, fraud, and abuse. Legal basis: our legitimate interests in protecting the service and its users (Article 6(1)(f) GDPR) and compliance with legal obligations (Article 6(1)(c) GDPR).',
    purposeLegalLabel: 'Legal Compliance:',
    purposeLegal:
      'To comply with applicable laws, regulations, legal processes, and governmental requests. Legal basis: compliance with legal obligations (Article 6(1)(c) GDPR).',
    purposesProcessorNote:
      'Where customer support conversations are processed through our platform on behalf of our business customers, the business customer is the data controller and Open processes that data as a processor under the applicable data processing agreement.',
    rightsH2: 'Your Rights Regarding Personal Data',
    rightsIntro: 'You have the following rights regarding your personal data:',
    rightsAccessH3: 'Right of Access',
    rightsAccessP:
      'You have the right to request confirmation of whether we process your personal data and to obtain a copy of that data.',
    rightsCorrectionH3: 'Right to Correction',
    rightsCorrectionP:
      'You have the right to request correction of inaccurate personal data or completion of incomplete personal data we hold about you.',
    rightsDeletionH3: 'Right to Deletion',
    rightsDeletionP:
      'You have the right to request deletion of your personal data, subject to certain legal exceptions (such as data required for legal compliance or to complete a transaction you initiated).',
    rightsObjectH3: 'Right to Object and Restrict Processing',
    rightsObjectP:
      'You have the right to object to processing of your personal data based on legitimate interests, and to request restriction of processing in certain circumstances.',
    rightsPortabilityH3: 'Right to Data Portability',
    rightsPortabilityP:
      'You have the right to receive your personal data in a structured, commonly used, and machine-readable format, and to transmit that data to another controller.',
    rightsOptOutH3: 'Right to Opt Out',
    rightsOptOutP:
      'You may opt out of receiving marketing communications at any time by clicking the "unsubscribe" link in our emails or by contacting us directly.',
    rightsWithdrawH3: 'Right to Withdraw Consent',
    rightsWithdrawP:
      'Where processing is based on your consent, you have the right to withdraw that consent at any time. Withdrawal does not affect the lawfulness of processing carried out before the withdrawal.',
    rightsAutomatedH3: 'Rights Related to Automated Decision-Making',
    rightsAutomatedP:
      'You have the right not to be subject to a decision based solely on automated processing, including profiling, that produces legal effects concerning you or similarly significantly affects you. Where such processing takes place, you may request human intervention, express your point of view, and contest the decision by contacting us at privacy@open.cx.',
    rightsExerciseH3: 'How to Exercise Your Rights',
    rightsExerciseP: 'To exercise any of these rights, please contact us at:',
    rightsEmailLabel: 'Email:',
    rightsMail:
      'Mail: OpenChat, Inc., Attn: Privacy Officer, 108 W. 13th Street, Suite 100, Wilmington, DE 19801-1145, United States',
    rightsRespond:
      'We will respond without undue delay and within 30 days or one calendar month of receiving your request, whichever is earlier. If an extension is permitted because of the complexity or number of requests, we will tell you why within the first month.',
    sharingH2: 'Sharing Your Information',
    sharingIntro:
      'Open may transfer personal data to third-party service providers (subprocessors) that perform services on our behalf. These third parties include:',
    sharingCloud: 'Cloud hosting and infrastructure providers',
    sharingPayment: 'Payment processors',
    sharingAnalytics: 'Analytics providers',
    sharingSupport: 'Customer support tools',
    sharingEmail: 'Email service providers',
    intlH2: 'International Data Transfers',
    intlP:
      'Personal data from the EEA, UK, and Switzerland may be transferred to countries outside those regions. Where a transfer requires safeguards, we use the applicable Standard Contractual Clauses and UK transfer terms under our agreements.',
    securityH2: 'Data Security',
    securityIntro:
      'We implement appropriate technical and organizational measures to protect personal data against unauthorized access, alteration, disclosure, or destruction. These measures include:',
    securityEncryption: 'Encryption of data in transit and at rest',
    securityAccess: 'Access controls and authentication mechanisms',
    securityAudits: 'Regular security assessments and audits',
    securityTraining: 'Employee training on data protection',
    securityIncident: 'Incident response procedures',
    securityOutro:
      'While we strive to protect your personal data, no security system is impenetrable. We cannot guarantee the absolute security of your data.',
    retentionH2: 'Data Retention',
    retentionIntro:
      'We retain personal data only for as long as necessary to fulfill the purposes for which it was collected, including:',
    retentionLi1: 'To provide our services and maintain your account;',
    retentionLi2: 'To comply with legal obligations;',
    retentionLi3: 'To resolve disputes and enforce agreements; and',
    retentionLi4: 'For legitimate business purposes such as fraud prevention.',
    retentionOutro:
      'When personal data is no longer needed, we securely delete or anonymize it.',
    childrenH2: "Children's Privacy",
    childrenA:
      'Our services are not directed to individuals under the age of 16. We do not knowingly collect personal data from children. If you believe a child has provided us with personal data, please contact us at',
    childrenB: ', and we will delete such data.',
    smsH2: 'SMS and Text Messaging',
    smsIntro:
      'When you subscribe to receive text messages from Open, we collect your mobile phone number and a record of your consent. We use them only to send you the messages you subscribed to, to respond to you, and to honor your opt-out requests.',
    smsNoShare:
      'We do not share, sell, or provide your mobile phone number or messaging consent data to third parties or affiliates for marketing or promotional purposes.',
    smsFrequency:
      'Message frequency varies and depends on your activity, typically fewer than five messages per support request. Message and data rates may apply.',
    smsOptOut:
      'You can opt out at any time by replying STOP to any message, and you can reply HELP to any message for assistance. Opting out stops all further messages from that program.',
    changesH2: 'Changes to This Privacy Policy',
    changesIntro:
      'We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors. When we make material changes, we will:',
    changesLi1: 'Update the "Last updated" date at the top of this policy;',
    changesLi2: 'Notify you via email or prominent notice on our website; and',
    changesLi3: 'Obtain your consent where required by applicable law.',
    changesOutro:
      'We encourage you to review this policy periodically to stay informed about how we protect your data.',
    contactH2: 'Contact Us',
    contactIntro:
      'If you have questions, concerns, or complaints about this Privacy Policy or our data practices, please contact us:',
    contactAttn: 'Attn: Privacy Officer',
    contactAddress:
      '108 W. 13th Street, Suite 100, Wilmington, DE 19801-1145, United States',
    contactEmailLabel: 'Email:',
    euRepresentativeH3: 'EU representative',
    ukRepresentativeH3: 'UK representative',
    representativeIntro:
      'You may contact our appointed representative about personal data processed under the EU or UK GDPR:',
    commitmentH2: 'Our Commitment',
    commitmentIntro:
      'Open is committed to protecting the privacy and security of personal data. We will:',
    commitmentLi1: 'Process personal data lawfully, fairly, and transparently;',
    commitmentLi2:
      'Collect data only for specified, explicit, and legitimate purposes;',
    commitmentLi3:
      'Ensure data is adequate, relevant, and limited to what is necessary;',
    commitmentLi4: 'Maintain accurate and up-to-date data;',
    commitmentLi5: 'Retain data only as long as necessary; and',
    commitmentLi6: 'Ensure appropriate security of personal data.',
    commitmentOutro:
      'By using our services, you acknowledge that you have read and understood this Privacy Policy.',
  },
  ar: {
    title: 'سياسة الخصوصية لدى Open',
    lastUpdated: 'آخر تحديث: ٢٨ سبتمبر ٢٠٢٦',
    introA:
      'توضّح سياسة الخصوصية هذه كيف تجمع OpenChat, Inc. ("Open" أو "نحن" أو "لدينا") البيانات الشخصية وتستخدمها وتشاركها وتحميها عند زيارتك لموقع',
    introB:
      '، أو استخدامك منصّتنا لدعم العملاء بالذكاء الاصطناعي، أو تعاملك مع خدماتنا بأي شكل. وتصف فئات المعلومات التي نعالجها، ولماذا نعالجها، والأسس القانونية التي نستند إليها، ومدة احتفاظنا بالبيانات، والأطراف الخارجية التي نشاركها معها، وحقوقك في الاطّلاع على معلوماتك أو تصحيحها أو حذفها أو تقييد استخدامنا لها. وتلتزم Open بالتعامل مع البيانات الشخصية بشفافية وبما يتوافق مع أنظمة الخصوصية العالمية، بما فيها EU GDPR وUK GDPR وSwiss FADP وقانون خصوصية المستهلك في كاليفورنيا (CCPA/CPRA) وما يماثلها من أنظمة حول العالم. ولا نعالج البيانات الشخصية إلا استنادًا إلى أساس قانوني — موافقتك، أو تنفيذ عقد، أو مصالحنا المشروعة، أو التزام قانوني — وحيثما نعتمد على الموافقة (كما في ملفات تعريف الارتباط غير الضرورية)، يمكنك سحبها في أي وقت. وإذا كانت لديك أسئلة حول أي جزء من هذه السياسة، تواصل مع مسؤول الخصوصية لدينا على',
    introC: 'لأي أسئلة أو استفسارات.',
    catH2: 'فئات البيانات الشخصية التي نجمعها',
    catIntro: 'نجمع ونعالج الفئات التالية من البيانات الشخصية:',
    catH31: 'المعلومات التي تقدّمها مباشرة',
    catAccountLabel: 'معلومات الحساب:',
    catAccount:
      'الاسم، وعنوان الإيميل، ورقم الهاتف، واسم الشركة، والمسمّى الوظيفي، وكلمة المرور عند إنشائك حساباً أو طلبك عرضاً توضيحياً.',
    catPaymentLabel: 'معلومات الدفع:',
    catPayment:
      'عنوان الفوترة، وتفاصيل بطاقة الدفع (تعالجها جهات معالجة الدفع لدينا)، وسجل المعاملات.',
    catCommsLabel: 'المراسلات:',
    catComms: 'محتوى الرسائل وتذاكر الدعم والملاحظات التي ترسلها إلينا.',
    catSupportLabel: 'بيانات دعم العملاء:',
    catSupport:
      'المعلومات الواردة في محادثات دعم العملاء التي تُعالج عبر منصّتنا نيابةً عن عملائنا من الشركات.',
    catH32: 'المعلومات التي تُجمع تلقائياً',
    catDeviceLabel: 'معلومات الجهاز:',
    catDevice:
      'عنوان IP، ونوع المتصفّح، ونظام التشغيل، ومعرّفات الجهاز، وإعدادات العتاد.',
    catUsageLabel: 'بيانات الاستخدام:',
    catUsage:
      'الصفحات التي تزورها، والميزات التي تستخدمها، والوقت الذي تقضيه على الصفحات، وأنماط النقر، وعناوين URL المُحيلة.',
    catCookiesLabel: 'ملفات تعريف الارتباط والتقنيات المشابهة:',
    catCookies:
      'ملفات تعريف الارتباط الخاصة بالجلسة، والدائمة، والتقنيات المشابهة للتتبّع كما هو موضّح في',
    catCookiesLinkText: 'سياسة ملفات تعريف الارتباط لدينا',
    catH33: 'المعلومات الواردة من أطراف خارجية',
    catIntegrationLabel: 'بيانات التكامل:',
    catIntegration:
      'المعلومات الواردة من الخدمات الخارجية التي تربطها بـ Open (مثل Zendesk وIntercom وSalesforce).',
    catBusinessLabel: 'معلومات الاتصال التجارية:',
    catBusiness:
      'تفاصيل الاتصال المهنية من شركاء الأعمال أو من المصادر المتاحة للعموم.',
    purposesH2: 'أغراض معالجة البيانات الشخصية',
    purposesIntro:
      'نعالج البيانات الشخصية للأغراض التالية، ونبيّن لكل غرض الأساس القانوني الذي نستند إليه بموجب EU GDPR وUK GDPR:',
    purposeServiceLabel: 'تقديم الخدمة:',
    purposeService:
      'لتقديم منصّتنا لدعم العملاء بالذكاء الاصطناعي والخدمات المرتبطة بها وصيانتها وتحسينها. الأساس القانوني: تنفيذ العقد (المادة 6(1)(ب) من GDPR).',
    purposeAccountLabel: 'إدارة الحساب:',
    purposeAccount:
      'لإنشاء حسابات المستخدمين وإدارتها، والتحقّق من هويّة المستخدمين، ومعالجة المعاملات. الأساس القانوني: تنفيذ العقد (المادة 6(1)(ب) من GDPR).',
    purposeSupportLabel: 'دعم العملاء:',
    purposeSupport:
      'للردّ على الاستفسارات، وتقديم المساعدة التقنية، وحلّ المشكلات. الأساس القانوني: تنفيذ العقد ومصالحنا المشروعة في تقديم دعم فعّال (المادتان 6(1)(ب) و6(1)(و) من GDPR).',
    purposeCommsLabel: 'المراسلات:',
    purposeComms:
      'لإرسال الإشعارات المتعلّقة بالخدمة، والتحديثات، وتنبيهات الأمان، والرسائل الإدارية. الأساس القانوني: تنفيذ العقد ومصالحنا المشروعة في إبقاء المستخدمين على اطّلاع بالخدمة (المادتان 6(1)(ب) و6(1)(و) من GDPR).',
    purposeMarketingLabel: 'التسويق:',
    purposeMarketing:
      'لإرسال المراسلات الترويجية (بموافقتك عند الحاجة) التي يمكنك إلغاء الاشتراك فيها في أي وقت. الأساس القانوني: موافقتك (المادة 6(1)(أ) من GDPR)، ويمكنك سحبها في أي وقت.',
    purposeAnalyticsLabel: 'التحليلات والتحسين:',
    purposeAnalytics:
      'لتحليل أنماط الاستخدام، ومعالجة المشكلات، وتحسين خدماتنا. الأساس القانوني: مصالحنا المشروعة في فهم استخدام الخدمة وتحسينها (المادة 6(1)(و) من GDPR)، والموافقة حيثما تُطلب لملفات تعريف الارتباط غير الضرورية.',
    purposeSecurityLabel: 'الأمان ومنع الاحتيال:',
    purposeSecurity:
      'لاكتشاف الحوادث الأمنية والاحتيال وإساءة الاستخدام ومنعها والتصدّي لها. الأساس القانوني: مصالحنا المشروعة في حماية الخدمة ومستخدميها (المادة 6(1)(و) من GDPR) والامتثال للالتزامات القانونية (المادة 6(1)(ج) من GDPR).',
    purposeLegalLabel: 'الامتثال القانوني:',
    purposeLegal:
      'للامتثال للأنظمة واللوائح المعمول بها والإجراءات القانونية والطلبات الحكومية. الأساس القانوني: الامتثال للالتزامات القانونية (المادة 6(1)(ج) من GDPR).',
    purposesProcessorNote:
      'وعندما تُعالَج محادثات دعم العملاء عبر منصّتنا نيابةً عن عملائنا من الشركات، يكون العميل التجاري هو المتحكّم بالبيانات وتعالج Open تلك البيانات بصفتها معالِجاً بموجب اتفاقية معالجة البيانات المعمول بها.',
    rightsH2: 'حقوقك المتعلّقة بالبيانات الشخصية',
    rightsIntro: 'لك الحقوق التالية فيما يتعلّق ببياناتك الشخصية:',
    rightsAccessH3: 'حق الاطّلاع',
    rightsAccessP:
      'لك الحق في طلب تأكيد ما إذا كنا نعالج بياناتك الشخصية والحصول على نسخة من تلك البيانات.',
    rightsCorrectionH3: 'حق التصحيح',
    rightsCorrectionP:
      'لك الحق في طلب تصحيح البيانات الشخصية غير الدقيقة أو استكمال البيانات الشخصية الناقصة التي نحتفظ بها عنك.',
    rightsDeletionH3: 'حق الحذف',
    rightsDeletionP:
      'لك الحق في طلب حذف بياناتك الشخصية، مع مراعاة بعض الاستثناءات القانونية (مثل البيانات اللازمة للامتثال القانوني أو لإتمام معاملة بدأتها).',
    rightsObjectH3: 'حق الاعتراض وتقييد المعالجة',
    rightsObjectP:
      'لك الحق في الاعتراض على معالجة بياناتك الشخصية المستندة إلى المصالح المشروعة، وفي طلب تقييد المعالجة في ظروف معيّنة.',
    rightsPortabilityH3: 'حق نقل البيانات',
    rightsPortabilityP:
      'لك الحق في الحصول على بياناتك الشخصية بصيغة منظّمة وشائعة الاستخدام وقابلة للقراءة آلياً، وفي نقل تلك البيانات إلى جهة تحكّم أخرى.',
    rightsOptOutH3: 'حق إلغاء الاشتراك',
    rightsOptOutP:
      'يمكنك إلغاء اشتراكك في تلقّي المراسلات التسويقية في أي وقت عن طريق النقر على رابط "إلغاء الاشتراك" في رسائلنا أو عن طريق التواصل معنا مباشرة.',
    rightsWithdrawH3: 'حق سحب الموافقة',
    rightsWithdrawP:
      'عندما تستند المعالجة إلى موافقتك، لك الحق في سحب تلك الموافقة في أي وقت. ولا يؤثّر السحب على قانونية المعالجة التي تمّت قبله.',
    rightsAutomatedH3: 'الحقوق المتعلّقة بالقرارات المؤتمتة والتنميط',
    rightsAutomatedP:
      'لك الحق في ألّا تخضع لقرار يستند فقط إلى معالجة مؤتمتة، بما في ذلك التنميط، يُنتج آثاراً قانونية تخصّك أو يؤثّر فيك تأثيراً كبيراً مماثلاً. وعند حدوث مثل هذه المعالجة، يمكنك طلب تدخّلاً بشرياً، وتعبّر عن وجهة نظرك، وتعترض على القرار بالتواصل معنا على privacy@open.cx.',
    rightsExerciseH3: 'كيف تمارس حقوقك',
    rightsExerciseP: 'لممارسة أي من هذه الحقوق، يرجى التواصل معنا على:',
    rightsEmailLabel: 'الإيميل:',
    rightsMail:
      'البريد: OpenChat, Inc., Attn: Privacy Officer, 108 W. 13th Street, Suite 100, Wilmington, DE 19801-1145, United States',
    rightsRespond:
      'سنردّ على طلبك دون تأخير غير مبرر وخلال ٣٠ يوماً أو شهر تقويمي واحد من استلامه، أيهما أقرب. وإذا سُمح بتمديد المهلة بسبب تعقيد الطلبات أو عددها، فسنبلغك بالسبب خلال الشهر الأول.',
    sharingH2: 'مشاركة معلوماتك',
    sharingIntro:
      'قد تنقل Open البيانات الشخصية إلى مزوّدي خدمات من أطراف خارجية (المعالِجون الفرعيون) الذين يؤدّون خدمات نيابةً عنا. وتشمل هذه الأطراف الخارجية:',
    sharingCloud: 'مزوّدي الاستضافة السحابية والبنية التحتية',
    sharingPayment: 'جهات معالجة الدفع',
    sharingAnalytics: 'مزوّدي التحليلات',
    sharingSupport: 'أدوات دعم العملاء',
    sharingEmail: 'مزوّدي خدمة الإيميل',
    intlH2: 'عمليات نقل البيانات الدولية',
    intlP:
      'قد تُنقل البيانات الشخصية من المنطقة الاقتصادية الأوروبية والمملكة المتحدة وسويسرا إلى دول خارج تلك المناطق. وحيثما تتطلب عمليات النقل ضمانات، نستخدم الشروط التعاقدية القياسية المعمول بها وشروط نقل البيانات الخاصة بالمملكة المتحدة بموجب اتفاقياتنا.',
    securityH2: 'أمان البيانات',
    securityIntro:
      'نطبّق تدابير تقنية وتنظيمية مناسبة لحماية البيانات الشخصية من الوصول غير المصرّح به أو التعديل أو الإفصاح أو الإتلاف. وتشمل هذه التدابير:',
    securityEncryption: 'تشفير البيانات أثناء النقل وفي حالة السكون',
    securityAccess: 'ضوابط الوصول وآليات المصادقة',
    securityAudits: 'تقييمات ومراجعات أمنية دورية',
    securityTraining: 'تدريب الموظفين على حماية البيانات',
    securityIncident: 'إجراءات الاستجابة للحوادث',
    securityOutro:
      'ومع أننا نحرص على حماية بياناتك الشخصية، لا يوجد نظام أمني منيع تماماً. ولا يمكننا ضمان الأمان المطلق لبياناتك.',
    retentionH2: 'الاحتفاظ بالبيانات',
    retentionIntro:
      'نحتفظ بالبيانات الشخصية فقط للمدة اللازمة لتحقيق الأغراض التي جُمعت من أجلها، بما في ذلك:',
    retentionLi1: 'لتقديم خدماتنا والحفاظ على حسابك؛',
    retentionLi2: 'للامتثال للالتزامات القانونية؛',
    retentionLi3: 'لحلّ النزاعات وإنفاذ الاتفاقيات؛ و',
    retentionLi4: 'لأغراض تجارية مشروعة مثل منع الاحتيال.',
    retentionOutro:
      'عندما لا تعود البيانات الشخصية لازمة، نحذفها أو نجعلها مجهولة الهوية بشكل آمن.',
    childrenH2: 'خصوصية الأطفال',
    childrenA:
      'خدماتنا ليست موجّهة للأفراد دون سن ١٦ عاماً. ولا نجمع عن قصد بيانات شخصية من الأطفال. وإذا كنت تعتقد أن طفلاً قد زوّدنا ببيانات شخصية، يرجى التواصل معنا على',
    childrenB: '، وسنحذف تلك البيانات.',
    smsH2: 'الرسائل النصية القصيرة (SMS)',
    smsIntro:
      'عند اشتراكك في استقبال الرسائل النصية من Open، نجمع رقم هاتفك الجوال وسجلّ موافقتك. ونستخدمهما فقط لإرسال الرسائل التي اشتركت بها، وللردّ عليك، ولتنفيذ طلبات إلغاء الاشتراك.',
    smsNoShare:
      'لا نشارك رقم هاتفك الجوال أو بيانات موافقتك على الرسائل ولا نبيعها ولا نتيحها لأي طرف ثالث أو شركة تابعة لأغراض التسويق أو الترويج.',
    smsFrequency:
      'يختلف عدد الرسائل بحسب نشاطك، وعادةً يكون أقل من خمس رسائل لكل طلب دعم. وقد تُطبَّق رسوم الرسائل والبيانات.',
    smsOptOut:
      'يمكنك إلغاء الاشتراك في أي وقت بالردّ بكلمة STOP على أي رسالة، كما يمكنك الردّ بكلمة HELP للحصول على المساعدة. ويؤدي إلغاء الاشتراك إلى إيقاف جميع الرسائل اللاحقة من ذلك البرنامج.',
    changesH2: 'التغييرات على سياسة الخصوصية هذه',
    changesIntro:
      'قد نحدّث سياسة الخصوصية هذه من وقت لآخر لتعكس تغييرات في ممارساتنا أو تقنياتنا أو المتطلّبات القانونية أو عوامل أخرى. وعند إجراء تغييرات جوهرية، فإننا:',
    changesLi1: 'نحدّث تاريخ "آخر تحديث" في أعلى هذه السياسة؛',
    changesLi2: 'نبلّغك عبر الإيميل أو بإشعار بارز على موقعنا؛ و',
    changesLi3: 'نحصل على موافقتك حيثما يتطلّب النظام المعمول به ذلك.',
    changesOutro:
      'نشجّعك على مراجعة هذه السياسة دورياً لتبقى مطّلعاً على كيفية حمايتنا لبياناتك.',
    contactH2: 'تواصل معنا',
    contactIntro:
      'إذا كانت لديك أسئلة أو استفسارات أو شكاوى بخصوص سياسة الخصوصية هذه أو ممارساتنا في التعامل مع البيانات، يرجى التواصل معنا:',
    contactAttn: 'عناية: مسؤول الخصوصية',
    contactAddress:
      '108 W. 13th Street, Suite 100, Wilmington, DE 19801-1145, United States',
    contactEmailLabel: 'الإيميل:',
    euRepresentativeH3: 'ممثّلنا في الاتحاد الأوروبي',
    ukRepresentativeH3: 'ممثّلنا في المملكة المتحدة',
    representativeIntro:
      'يمكنك التواصل مع ممثّلنا المعنيّ بشأن البيانات الشخصية التي تُعالَج بموجب نظام حماية البيانات في الاتحاد الأوروبي أو المملكة المتحدة:',
    commitmentH2: 'التزامنا',
    commitmentIntro: 'تلتزم Open بحماية خصوصية وأمان البيانات الشخصية. وسوف:',
    commitmentLi1: 'نعالج البيانات الشخصية بشكل قانوني ونزيه وشفّاف؛',
    commitmentLi2: 'نجمع البيانات فقط لأغراض محدّدة وصريحة ومشروعة؛',
    commitmentLi3: 'نتأكّد أن البيانات كافية وذات صلة ومقتصرة على ما هو ضروري؛',
    commitmentLi4: 'نحافظ على بيانات دقيقة ومحدَّثة؛',
    commitmentLi5: 'نحتفظ بالبيانات فقط للمدة اللازمة؛ و',
    commitmentLi6: 'نضمن أماناً مناسباً للبيانات الشخصية.',
    commitmentOutro:
      'باستخدامك خدماتنا، فإنك تقرّ بأنك قرأت سياسة الخصوصية هذه وفهمتها.',
  },
} as const;

const ARABIC_INDIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';

const PrivacyPolicyPage = () => {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  // Section numbers are authored as Latin digits. Arabic prose on this site
  // uses Arabic-Indic digits (see /ar/terms), so convert the glyphs — never
  // the value — when rendering the Arabic locale.
  const num = (value: string): string =>
    locale === 'ar'
      ? value.replace(/[0-9]/g, (d) => ARABIC_INDIC_DIGITS[Number(d)])
      : value;

  return (
    <section className="py-32">
      <div className="container max-w-4xl">
        <div className="mb-12 flex flex-col gap-6">
          <Badge variant="outline" className="w-fit text-black">
            {t.footer['Legal']}
          </Badge>
          <h1 className="font-serif text-4xl text-black md:text-5xl">
            {c.title}
          </h1>
          <p className="text-muted-foreground text-sm">{c.lastUpdated}</p>
          <p className="max-w-3xl text-base leading-relaxed text-neutral-700">
            {c.introA}{' '}
            <a
              href="https://www.open.cx"
              className="text-primary hover:underline"
            >
              open.cx
            </a>
            {c.introB}{' '}
            <a
              href="mailto:privacy@open.cx"
              className="text-primary hover:underline"
            >
              privacy@open.cx
            </a>{' '}
            {c.introC}
          </p>
        </div>

        <div className="prose prose-sm prose-slate dark:prose-invert max-w-none text-black">
          {/* Categories of Personal Data */}
          <h2>
            {num('1.')} {c.catH2}
          </h2>
          <p>{c.catIntro}</p>
          <h3>
            {num('1.1')} {c.catH31}
          </h3>
          <ul>
            <li>
              <strong>{c.catAccountLabel}</strong> {c.catAccount}
            </li>
            <li>
              <strong>{c.catPaymentLabel}</strong> {c.catPayment}
            </li>
            <li>
              <strong>{c.catCommsLabel}</strong> {c.catComms}
            </li>
            <li>
              <strong>{c.catSupportLabel}</strong> {c.catSupport}
            </li>
          </ul>
          <h3>
            {num('1.2')} {c.catH32}
          </h3>
          <ul>
            <li>
              <strong>{c.catDeviceLabel}</strong> {c.catDevice}
            </li>
            <li>
              <strong>{c.catUsageLabel}</strong> {c.catUsage}
            </li>
            <li>
              <strong>{c.catCookiesLabel}</strong> {c.catCookies}{' '}
              <Link
                href="/cookie-policy"
                className="text-primary hover:underline"
              >
                {c.catCookiesLinkText}
              </Link>
              .
            </li>
          </ul>
          <h3>
            {num('1.3')} {c.catH33}
          </h3>
          <ul>
            <li>
              <strong>{c.catIntegrationLabel}</strong> {c.catIntegration}
            </li>
            <li>
              <strong>{c.catBusinessLabel}</strong> {c.catBusiness}
            </li>
          </ul>

          {/* Purposes of Processing */}
          <h2>
            {num('2.')} {c.purposesH2}
          </h2>
          <p>{c.purposesIntro}</p>
          <ul>
            <li>
              <strong>{c.purposeServiceLabel}</strong> {c.purposeService}
            </li>
            <li>
              <strong>{c.purposeAccountLabel}</strong> {c.purposeAccount}
            </li>
            <li>
              <strong>{c.purposeSupportLabel}</strong> {c.purposeSupport}
            </li>
            <li>
              <strong>{c.purposeCommsLabel}</strong> {c.purposeComms}
            </li>
            <li>
              <strong>{c.purposeMarketingLabel}</strong> {c.purposeMarketing}
            </li>
            <li>
              <strong>{c.purposeAnalyticsLabel}</strong> {c.purposeAnalytics}
            </li>
            <li>
              <strong>{c.purposeSecurityLabel}</strong> {c.purposeSecurity}
            </li>
            <li>
              <strong>{c.purposeLegalLabel}</strong> {c.purposeLegal}
            </li>
          </ul>
          <p>{c.purposesProcessorNote}</p>

          {/* User Rights */}
          <h2>
            {num('3.')} {c.rightsH2}
          </h2>
          <p>{c.rightsIntro}</p>
          <h3>
            {num('3.1')} {c.rightsAccessH3}
          </h3>
          <p>{c.rightsAccessP}</p>
          <h3>
            {num('3.2')} {c.rightsCorrectionH3}
          </h3>
          <p>{c.rightsCorrectionP}</p>
          <h3>
            {num('3.3')} {c.rightsDeletionH3}
          </h3>
          <p>{c.rightsDeletionP}</p>
          <h3>
            {num('3.4')} {c.rightsObjectH3}
          </h3>
          <p>{c.rightsObjectP}</p>
          <h3>
            {num('3.5')} {c.rightsPortabilityH3}
          </h3>
          <p>{c.rightsPortabilityP}</p>
          <h3>
            {num('3.6')} {c.rightsOptOutH3}
          </h3>
          <p>{c.rightsOptOutP}</p>
          <h3>
            {num('3.7')} {c.rightsWithdrawH3}
          </h3>
          <p>{c.rightsWithdrawP}</p>
          <h3>
            {num('3.8')} {c.rightsAutomatedH3}
          </h3>
          <p>{c.rightsAutomatedP}</p>
          <h3>
            {num('3.9')} {c.rightsExerciseH3}
          </h3>
          <p>{c.rightsExerciseP}</p>
          <ul>
            <li>
              {c.rightsEmailLabel}{' '}
              <a
                href="mailto:privacy@open.cx"
                className="text-primary hover:underline"
              >
                privacy@open.cx
              </a>
            </li>
            <li>{c.rightsMail}</li>
          </ul>
          <p>{c.rightsRespond}</p>

          {/* Sharing Information */}
          <h2>
            {num('4.')} {c.sharingH2}
          </h2>
          <p>{c.sharingIntro}</p>
          <ul>
            <li>{c.sharingCloud}</li>
            <li>{c.sharingPayment}</li>
            <li>{c.sharingAnalytics}</li>
            <li>{c.sharingSupport}</li>
            <li>{c.sharingEmail}</li>
          </ul>

          {/* Data Security */}
          <h2>
            {num('5.')} {c.securityH2}
          </h2>
          <p>{c.securityIntro}</p>
          <ul>
            <li>{c.securityEncryption}</li>
            <li>{c.securityAccess}</li>
            <li>{c.securityAudits}</li>
            <li>{c.securityTraining}</li>
            <li>{c.securityIncident}</li>
          </ul>
          <p>{c.securityOutro}</p>

          {/* Data Retention */}
          <h2>
            {num('6.')} {c.retentionH2}
          </h2>
          <p>{c.retentionIntro}</p>
          <ul>
            <li>{c.retentionLi1}</li>
            <li>{c.retentionLi2}</li>
            <li>{c.retentionLi3}</li>
            <li>{c.retentionLi4}</li>
          </ul>
          <p>{c.retentionOutro}</p>

          {/* Children's Privacy */}
          <h2>
            {num('7.')} {c.childrenH2}
          </h2>
          <p>
            {c.childrenA}{' '}
            <a
              href="mailto:privacy@open.cx"
              className="text-primary hover:underline"
            >
              privacy@open.cx
            </a>
            {c.childrenB}
          </p>

          {/* International Transfers */}
          <h2>
            {num('8.')} {c.intlH2}
          </h2>
          <p>{c.intlP}</p>

          {/* SMS and Text Messaging */}
          <h2>
            {num('9.')} {c.smsH2}
          </h2>
          <p>{c.smsIntro}</p>
          <p>{c.smsNoShare}</p>
          <p>{c.smsFrequency}</p>
          <p>{c.smsOptOut}</p>

          {/* Policy Changes */}
          <h2>
            {num('10.')} {c.changesH2}
          </h2>
          <p>{c.changesIntro}</p>
          <ul>
            <li>{c.changesLi1}</li>
            <li>{c.changesLi2}</li>
            <li>{c.changesLi3}</li>
          </ul>
          <p>{c.changesOutro}</p>

          {/* Contact Information */}
          <h2>
            {num('11.')} {c.contactH2}
          </h2>
          <p>{c.contactIntro}</p>
          <p>
            <strong>OpenChat, Inc.</strong>
            <br />
            {c.contactAttn}
            <br />
            {c.contactAddress}
            <br />
            {c.contactEmailLabel}{' '}
            <a
              href="mailto:privacy@open.cx"
              className="text-primary hover:underline"
            >
              privacy@open.cx
            </a>
          </p>
          <p>{c.representativeIntro}</p>
          <h3>{c.euRepresentativeH3}</h3>
          <p>
            <span dir="ltr">Instant EU GDPR Representative Limited</span>
            <br />
            <span dir="ltr">
              Office 2, 12A Lower Main Street, Lucan, Co. Dublin, K78 X5P8,
              Ireland
            </span>
            <br />
            <a href="tel:+35315549700" className="text-primary hover:underline">
              +353 (0) 1 554 9700
            </a>
            <br />
            <a
              href="mailto:contact@gdprlocal.com"
              className="text-primary hover:underline"
            >
              contact@gdprlocal.com
            </a>
          </p>
          <h3>{c.ukRepresentativeH3}</h3>
          <p>
            <span dir="ltr">GDPR Local Ltd</span>
            <br />
            <span dir="ltr">
              1st Floor Front Suite, 27-29 North Street, Brighton, BN1 1EB,
              England
            </span>
            <br />
            <a
              href="tel:+441772217800"
              className="text-primary hover:underline"
            >
              +44 (0) 1772 217800
            </a>
            <br />
            <a
              href="mailto:contact@gdprlocal.com"
              className="text-primary hover:underline"
            >
              contact@gdprlocal.com
            </a>
          </p>

          {/* Commitment Statement */}
          <h2>
            {num('12.')} {c.commitmentH2}
          </h2>
          <p>{c.commitmentIntro}</p>
          <ul>
            <li>{c.commitmentLi1}</li>
            <li>{c.commitmentLi2}</li>
            <li>{c.commitmentLi3}</li>
            <li>{c.commitmentLi4}</li>
            <li>{c.commitmentLi5}</li>
            <li>{c.commitmentLi6}</li>
          </ul>
          <p>{c.commitmentOutro}</p>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicyPage;
